<template>
  <v-dialog
    v-if="isLoaded"
    v-model="modalState"
    width="900"
    persistent
  >
    <v-card :class="highlightForm && 'highlight-required-fields'">
      <v-form
        ref="form"
        v-model="formValid"
        :class="isSigned ? 'form-disabled' : ''"
        :disabled="isSigned"
      >
        <div
          ref="woundTitle"
          class="sticky-header"
        >
          <PatientInformation
            :title="`#${wound.wound_number} Wound Detail Entry`"
            :patient="patient"
            :visit-date="encounterVisitDate"
          >
            <!-- image attachments -->
            <btn
              label="Images"
              :icon="icons.mdiFileImage"
              class="px-5"
              @click="attachmentTypeClicked"
            >
              <v-badge
                v-if="imageAttachmentCount"
                color="primary"
                :content="imageAttachmentCount"
                inline
              >
                Images
              </v-badge>
            </btn>

            <v-spacer></v-spacer>
            <!-- best practice guidelines -->
            <BestPractice></BestPractice>
          </PatientInformation>

          <!-- attachment modal -->
          <attachment-modal
            :attachment-modal.sync="attachmentModal"
            :attachment-type.sync="attachmentType"
            attachment-type-id="3"
            :encounter-attachments.sync="patient.all_attachments"
            :patient-id="patient.id"
            :wound-id="wound.id"
            :signed="isSigned"
            :images-only="true"
          ></attachment-modal>
        </div>

        <NoteSection
          ref="LocationEtiology"
          label="Location &amp; Etiology"
          required
          :highlight-required="isLocationEtiologyIncomplete"
          :form-validated="formValidated"
        >
          <!-- location -->
          <text-field
            :key="wound.location.location_text"
            ref="woundLoc"
            :value="wound.location.location_text"
            class="col-sm-7"
            label="Wound Location"
            maxlength="300"
            readonly
            readonly-validate
            required
            @clear-warning="true"
            @click="onLocationClick"
          />

          <!-- etiology -->
          <auto-complete-box
            :value="treatment.etiology"
            :items="woundEtiologies"
            label="Etiology"
            item-value="title"
            class="col-sm-5"
            :previous-value="latestTreatment && latestTreatment.etiology ? previousEtiology: null"
            :previous-value-dos="previousVisitDate"
            required
            @change="etiologyChanged"
          ></auto-complete-box>

          <text-field
            v-if="treatment.etiology === 'Other'"
            :value="treatment.etiology_other"
            label="Enter other etiology..."
            class="col-sm-12"
            counter="900"
            max-length="900"
            required
            :previous-value="latestTreatment && latestTreatment.etiology_other
              ? latestTreatment.etiology_other : null"
            :previous-value-dos="previousVisitDate"
            :input-history-custom-styles="'padding-top: 18px;'"
            @blur="etiologyOtherChanged"
            @click:clear="etiologyOtherChanged"
          ></text-field>

          <!-- Warnings: Allow validation -->
          <v-expand-transition>
            <div v-if="isLocationOnLeg && treatment.etiology === 'Pressure Injury/Ulcer' && isCirculationCompromised">
              <alert>
                As this patient is documented as having diabetes, PVD, PAD, or VSD and this is a lower extremity wound, please select a wound etiology accordingly.
                For example, It may be more accurate to select "Diabetes" instead of "Pressure Injury/Ulcer".
              </alert>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div v-if="isLocationOnLeg && treatment.etiology === 'Pressure Injury/Ulcer' && !isCirculationCompromised">
              <alert>
                As this is a lower extremity pressure injury/ulcer and this patient is NOT documented as having diabetes, PVD, PAD, or VSD, it is advised to order an arterial ultrasound for this patient.
              </alert>
            </div>
          </v-expand-transition>

          <!-- location modal -->
          <Location
            v-model="wound.location"
            :wound-id="wound.id"
            :modal-state.sync="locationModal"
            :wounds="patient.wounds"
            :wound="wound"
          ></Location>

          <ChangeLateralityModal
            :modal-state.sync="changeLateralityModal"
            :current-location-text="wound.location.location_text"
            @save="onChangeLateralitySave"
          ></ChangeLateralityModal>
        </NoteSection>

        <NoteSection label="Facility Acquired Wound">
          <!-- facility acquired wound -->
          <alert color="primary">
            This information does not show on the final note.
          </alert>
          <checkbox
            :value="wound.is_wound_facility_acquired"
            label="Is wound facility-acquired?"
            class="col-sm-5"
            hide-details
            @change="facilityAcquired"
          ></checkbox>
        </NoteSection>

        <div class="note-section mx-2">
          <!-- Alerts: Prevent validation (set color="error" and add required checkbox) -->
          <v-expand-transition>
            <div v-if="wound.is_healed && isProcedureDebridement">
              <alert color="error">
                Debridement procedure cannot be selected for this wound as it is indicated as being resolved.
                Please review the procedure selection or dimensions entered.
                <checkbox required></checkbox>
              </alert>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div v-if="wound.location.location_text && treatment.procedure_id">
              <!-- (wound.location.location === 19 && treatment.procedure_id === 183)
            || (isLocationOnFoot && !isProcedureFoot && treatment.procedure_id !== 183) -->

              <alert
                v-if="!isLowerLimb && treatment.procedure_id === 195"
                color="error"
              >
                The selected procedure can only be applied to locations of leg, malleolus, foot, ball of foot or ankle.
                <checkbox required></checkbox>
              </alert>

              <alert
                v-if="!isTorsoAndLimbs && treatment.procedure_id === 177"
                color="error"
              >
                The selected procedure can only be applied to locations of leg, arm, chest, back, abdomen, or shoulder.
                <checkbox required></checkbox>
              </alert>

              <alert
                v-if="!isLocationOnFoot && isProcedureFoot && treatment.procedure_id !== 195"
                color="error"
              >
                The selected procedure can only be applied to locations of ball of foot, foot, toe, or heel.
                <checkbox required></checkbox>
              </alert>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div
              v-if="Number(wound_treatment.wound_treatment_wound_size.granulation) === 100
                && !wound_treatment.wound_treatment_wound_size.unhealthy_granulation
                && !wound_treatment.preoperative_indications.some(x => x.title === 'Bioburden' || x.title === 'Biofilm')
                // checks if the treatment procedure is a debridement
                && isProcedureDebridement"
            >
              <alert color="error">
                Debridement on 100% granulation tissue is not permitted unless "Bioburden" or "Biofilm" are selected in the "Preoperative Indications" section or the "Unhealthy granulation tissue identified" checkbox is checked in the "Wound Measurements" section.
                <checkbox required></checkbox>
              </alert>
            </div>
          </v-expand-transition>
        </div>

        <NoteSection
          label="Procedure Performed"
          required
          :highlight-required="isProcedureIncomplete"
          :form-validated="formValidated"
        >
          <div
            v-if="visitLocation === 'Telemedicine'"
            style="width: 100%"
          >
            <alert color="warning">
              Procedure selection is disabled during telemedicine visits.
            </alert>
          </div>
          <auto-complete-box
            v-model="treatment.procedure_id"
            :items="woundProcedures"
            label="Procedure Performed"
            class="col-sm-12"
            dense
            single-line
            no-truncate
            required
            :disabled="visitLocation === 'Telemedicine'"
            :previous-value="previousProcedureTitle"
            :previous-value-dos="previousVisitDate"
          ></auto-complete-box>
        </NoteSection>
        <v-expand-transition>
          <NoteSection
            v-if="isProcedureAmnioticTissueGraft && isAmnioEnabled"
            label="Amniotic Tissue Graft"
          >
            <v-container>
              <v-row
                justify="center"
                align="center"
                class="text-center"
              >
                <v-col
                  v-if="!amnioValid && isAmnioTouched"
                  cols="12"
                  style="padding: 0 !important;"
                >
                  <checkbox
                    required
                    class="d-none"
                  />
                  <icon-value
                    :icon="icons.mdiInformation"
                    color="error"
                  >
                    Please complete all required fields in the Amniotic Tissue Graft section
                  </icon-value>
                </v-col>
                <v-col
                  cols="12"
                  class="pb-6"
                >
                  <!-- Amniotic Tissue Graft Button -->
                  <btn
                    label="Enter Amniotic Tissue Graft information..."
                    :icon="icons.mdiBandage"
                    class="col-sm-12 d-block mx-auto"
                    :color="amnioBtnColor"
                    :disabled="isSigned || isControlResearchType"
                    @click="onClickAmnio"
                  />
                </v-col>
              </v-row>
              <!-- Amniotic Tissue Graft Modal -->
              <AmnioticTissueGraft
                ref="amnioComponent"
                :modal-state.sync="amnioModal"
                :amnio="wound_treatment.wound_treatments_amnios"
                :amnio-grafts="wound_treatment.amnio_grafts"
                :fixations="wound_treatment.wound_treatment_amnio_fixations"
                :wound-id="treatment.wound_id"
                :treatment-id="treatment.id"
                :encounter-visit-date="encounterVisitDate"
                :amnio-duration-weeks.sync="wound.amnio_pre_treatment_duration_weeks"
                :is-pad-pvd.sync="isEtiologyPadPvd"
                :wound-current-area="wound_treatment.wound_treatment_wound_size.primary_area"
                :patient-attachments="patient.all_attachments"
                :patient-id="patient.id"
                :patient-amnio-consent.sync="patient.amnio_consent_obtained_from"
                :encounter-created-date="encounterCreatedDate"
                :form-validated="formValidated"
                @update:wound_treatments_amnios="wound_treatment.wound_treatments_amnios = $event"
                @update:amnio_grafts="wound_treatment.amnio_grafts = $event"
                @update:wound_treatment_amnio_fixations="wound_treatment.wound_treatment_amnio_fixations = $event"
                @update:patient-amnio-consent="patient.amnio_consent_obtained_from = $event"
                @update:amnio-form-valid="amnioValid = $event"
                @update:closed="closed = $event"
              >
              </AmnioticTissueGraft>
            </v-container>
          </NoteSection>
        </v-expand-transition>

        <v-expand-transition>
          <NoteSection
            v-if="(
              (
                (isControlResearchType && !isProcedureAmnioticTissueGraft && wound.amnio_mapped_wound_id)
                ||
                (isTestResearchType && isProcedureAmnioticTissueGraft && wound.amnio_mapped_wound_id)
              )
              && !isSigned
            )"
            label="Amniotic Tissue Graft Research"
          >
            <v-container>
              <v-row
                justify="center"
                align="center"
                class="text-center mt-1"
              >
                <v-col
                  cols="12"
                  class="col-sm-12 py-2 rounded-border d-flex"
                  style="background-color: #fbf2dc; border-radius: 6px"
                >
                  <div class="d-flex align-start">
                    <icon-value
                      :icon="icons.mdiInformation"
                      color="warning"
                      style="line-height: 30px"
                    />
                    <div class="text-left pl-3">
                      <strong>SWC has selected this patient to be part of a study.</strong><br>
                      <p class="mb-0">
                        Please click this button to enter some additional required information.
                      </p>
                    </div>
                  </div>
                </v-col>

                <v-col
                  v-if="!amnioResearchValid && isAmnioResearchTouched"
                  cols="12"
                  style="padding: 0 !important;"
                >
                  <checkbox
                    required
                    class="d-none"
                  />
                  <icon-value
                    :icon="icons.mdiInformation"
                    color="error"
                  >
                    Please complete all required fields in the Amniotic Research Information
                  </icon-value>
                </v-col>

                <v-col
                  cols="12"
                  class="col-sm-12 pb-7"
                >
                  <btn
                    label="Enter Additional Information..."
                    :icon="icons.mdiFileDocument"
                    class="col-sm-12 d-block mx-auto"
                    :color="amnioResearchBtnColor"
                    @click="onClickResearch"
                  />
                </v-col>

                <!-- ATG Research Modal -->
                <AmnioticResearchInformation
                  v-if="researchModal"
                  ref="researchComponent"
                  :modal-state.sync="researchModal"
                  :amnio-research.sync="wound_treatment.wound_treatments_amnios_research"
                  :amnio-research-symptoms.sync="wound_treatment.wound_treatments_amnios_researches_symptoms"
                  :amnio-research-patient-type="patient.amnio_research_patient_type"
                  :patient-name="`${patient.first_name} ${patient.last_name}`"
                  :treatment-id="treatment.id"
                  :facility-id="treatment.place_of_service_id"
                  :wound-site-pain-level.sync="wound_treatment.pain_level"
                  :wound-site-odor.sync="wound_treatment.odors"
                  :was-amnio-treated="wasAmnioTreated"
                  :is-test-group="isTestResearchType"
                  @update:amnio-research-form-valid="amnioResearchValid = $event"
                />
              </v-row>
            </v-container>
          </NoteSection>
        </v-expand-transition>
        <WoundMeasurements
          ref="woundMeasurements"
          v-model="wound_treatment.wound_treatment_wound_size"
          :treatments="wound.treatments"
          :debridement-not-performed-reasons="wound_treatment.debridement_not_performed_reasons"
          :is-healed.sync="wound.is_healed"
          :is-etiology-pressure="isEtiologyPressure"
          :highlight-form="highlightForm"
          :is-procedure-debridement="isProcedureDebridement"
          :procedure-id="treatment.procedure_id"
          :encounter-id="treatment.encounter_id"
          :wound-progress.sync="wound_treatment.progress"
          :latest-treatment="latestTreatment"
          :is-amniotic-procedure="isProcedureAmnioticTissueGraft"
          :user-id="userId"
          :is-signed="isSigned"
          :visit-date="encounterVisitDate"
          :form-validated="formValidated"
        ></WoundMeasurements>

        <NoteSection
          v-if="requireReasonForNoDebridement"
          label="Reasons For Debridement Not Performed"
          :required="requireReasonForNoDebridement"
          :highlight-required="isReasonsForNoDebridementIncomplete"
          :form-validated="formValidated"
        >
          <check-boxes
            v-model="wound_treatment.debridement_not_performed_reasons"
            :items="debrNotPerformedValueList"
            class="col-sm-12"
            columns="2"
            :previous-value="hasPreviousWoundTreatment && previousDebridementNotPerformedReasons
              ? previousDebridementNotPerformedReasons : null"
            :previous-value-dos="previousVisitDate"
            column-sort
            return-object
            hide-details
            required
          ></check-boxes>
          <text-area
            v-if="wound_treatment.debridement_not_performed_reasons.some(x => x.title === 'Other')"
            v-model="wound_treatment.debridement_not_performed_reason_other"
            label="Enter other reason debridement was not performed..."
            class="col-sm-12"
            :previous-value="hasPreviousWoundTreatment && latestTreatment.wound_treatment.debridement_not_performed_reason_other
              ? latestTreatment.wound_treatment.debridement_not_performed_reason_other : null"
            :previous-value-dos="previousVisitDate"
            required
            counter="1600"
            maxlength="1600"
          ></text-area>
        </NoteSection>

        <NoteSection label="Wound Progress">
          <select-box
            v-model="wound_treatment.progress"
            :items="progresses"
            label="Wound Progress"
            class="col-sm-12"
            :previous-value="hasPreviousWoundTreatment ? previousWoundProgress : null"
            :previous-value-dos="previousVisitDate"
          >
          </select-box>

          <text-area
            v-if="wound_treatment.progress === 'Other'"
            v-model="wound_treatment.progress_other"
            label="Enter other wound progress..."
            class="col-sm-12"
            required
            counter="2200"
            maxlength="2200"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.progress_other : null"
            :previous-value-dos="previousVisitDate"
          ></text-area>

          <WoundNotHealingAttachments
            :patient-attachments.sync="patient.all_attachments"
            :patient-id="patient.id"
            :wound-id="wound.id"
            :is-poor-healing.sync="wound.is_poor_healing"
          ></WoundNotHealingAttachments>
        </NoteSection>

        <NoteSection
          label="Wound Description"
          heading
        ></NoteSection>

        <NoteSection
          v-if="!isQuickEntry"
          label="Odor"
        >
          <check-boxes
            v-model="wound_treatment.odors"
            :items="odors"
            class="col-sm-12 mt--2"
            columns="5"
            return-object
            radio-buttons
            clearable
            :previous-value="hasPreviousWoundTreatment && previousOdors
              ? previousOdors : null"
            :previous-value-dos="previousVisitDate"
          ></check-boxes>
        </NoteSection>

        <NoteSection
          label="Exudate"
          :required="isProcedureAmnioticTissueGraft"
          :highlight-required="isExudateIncomplete"
          :form-validated="formValidated"
        >
          <check-boxes
            v-model="selectedExudateAmounts"
            :items="exudateAmounts"
            label="Amount"
            class="col-sm-12 mt--5"
            columns="5"
            return-object
            radio-buttons
            clearable
            :previous-value="hasPreviousWoundTreatment && previousExudateTypesAndAmount
              ? previousExudateTypesAndAmount : null"
            :previous-value-dos="previousVisitDate"
            :required="isProcedureAmnioticTissueGraft"
            hide-details
            @change="exudateAmountChanged"
          ></check-boxes>

          <v-divider></v-divider>

          <check-boxes
            v-model="wound_treatment.exudate_types"
            :items="exudateTypes"
            :disabled="selectedExudateAmounts.some(x => x && x.title === 'None')"
            label="Type"
            class="col-sm-12"
            columns="3"
            column-sort
            return-object
            hide-details
            :required="isProcedureAmnioticTissueGraft"
          ></check-boxes>
        </NoteSection>

        <NoteSection
          v-if="!isQuickEntry"
          label="Periwound"
        >
          <check-boxes
            v-model="wound_treatment.periwounds"
            :items="periwounds"
            :distinct="['Stable', 'Healthy']"
            class="col-sm-12 mt--1"
            columns="3"
            column-sort
            return-object
            :previous-value="hasPreviousWoundTreatment && previousPeriwounds
              ? previousPeriwounds : null"
            :previous-value-dos="previousVisitDate"
            hide-details
          ></check-boxes>
        </NoteSection>

        <NoteSection
          v-if="!isQuickEntry"
          label="Wound Edge"
        >
          <check-boxes
            v-model="wound_treatment.wound_edges"
            :items="woundEdges"
            distinct="Normal"
            class="col-sm-12 mt--1"
            columns="3"
            column-sort
            return-object
            hide-details
            :previous-value="hasPreviousWoundTreatment && previousWoundEdges
              ? previousWoundEdges : null"
            :previous-value-dos="previousVisitDate"
          ></check-boxes>
        </NoteSection>

        <NoteSection
          label="Signs Of Infection"
          required
          :has-error="isSignsOfInfectionError"
          :highlight-required="isSignsOfInfectionIncomplete"
          :form-validated="formValidated"
        >
          <check-boxes
            v-model="wound_treatment.infection_signs"
            :items="infectionSigns"
            distinct="None"
            class="col-sm-12 mt--1"
            columns="3"
            column-sort
            return-object
            hide-details
            :previous-value="hasPreviousWoundTreatment && previousInfectionSigns
              ? previousInfectionSigns : null"
            :previous-value-dos="previousVisitDate"
            :required="!isQuickEntry"
          ></check-boxes>
        </NoteSection>

        <WoundWheel
          v-model="wound_treatment.wound_treatment_undermining"
          :tunneling-wheel.sync="wound_treatment.wound_treatment_tunneling"
          :sinus-tract-wheel.sync="wound_treatment.wound_treatment_sinus_tract"
          :is-signed="isSigned"
        ></WoundWheel>

        <NoteSection
          v-if="!isQuickEntry"
          label="Pain Level"
          :required="isProcedureAmnioticTissueGraft"
          :has-error="isPainLevelError"
          :highlight-required="isPainLevelIncomplete"
          :form-validated="formValidated"
        >
          <v-row justify="center" class="pain-level-radio">
            <radio-buttons
              v-model="wound_treatment.pain_level"
              :items="painLevel"
              class="mb-2"
              clearable
              :required="isProcedureAmnioticTissueGraft"
              no-asterisk
            ></radio-buttons>
          </v-row>
        </NoteSection>

        <NoteSection
          v-if="!isQuickEntry"
          label="Wound Description Summary"
        >
          <alert
            v-if="descriptionUpdated && !isWoundDescriptionAIDisabled"
            class="mx-2"
          >
            Update the wound description summary using the blue button below to reflect the changes made in the wound description section.
          </alert>
          <alert
            v-if="descriptionUpdated && isWoundDescriptionAIDisabled && wound_treatment.wound_description_summary"
            class="mx-2"
          >
            All wound description fields have been cleared. Please review the summary text below to confirm you want it cleared or make any adjustments manually.
          </alert>
          <text-area
            v-model="wound_treatment.wound_description_summary"
            label="Wound description summary"
            class="col-sm-12"
            counter="8500"
            maxlength="8500"
            ai
            :prepend-button-disabled="isWoundDescriptionAIDisabled"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.wound_description_summary : null"
            :previous-value-dos="previousVisitDate"
            @click:prepend="generateWoundDescription(false)"
          ></text-area>
        </NoteSection>

        <NoteSection
          v-if="!isQuickEntry && debridementProcedurePerformed"
          label="Preoperative Indications"
          required
          :has-error="isPreopIndicationError"
          :highlight-required="isPreopIndicationsIncomplete"
          :form-validated="formValidated"
        >
          <check-boxes
            v-model="wound_treatment.preoperative_indications"
            :items="preoperativeIndications"
            class="col-sm-12 mb-2"
            columns="2"
            column-sort
            return-object
            hide-details
            :previous-value="hasPreviousWoundTreatment ? previousPreoperativeIndications : null"
            :previous-value-dos="previousVisitDate"
            :required="!!treatment.procedure_id"
          ></check-boxes>
          <text-area
            v-if="isOtherPreoperativeIndication"
            v-model="wound_treatment.preoperative_indication_other"
            label="Enter other preoperative indications..."
            class="col-sm-12"
            required
            counter="1300"
            maxlength="1300"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.preoperative_indication_other : null"
            :previous-value-dos="previousVisitDate"
          ></text-area>
        </NoteSection>

        <NoteSection
          label="Wound Treatment"
          heading
        ></NoteSection>

        <NoteSection
          label="Dressings Used"
          required
          :highlight-required="isDressingsUsedIncomplete"
          :form-validated="formValidated"
        >
          <auto-complete-box
            v-model="wound_treatment.dressing_used_1"
            :items="dressings"
            label="Dressing Used"
            class="col-sm-6"
            :required="!isQuickEntry && this.$custom.isEmpty(wound_treatment.dressing_used_other)"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.dressing_used_1 : null"
            :previous-value-dos="previousVisitDate"
            @input="firstDressingUsedChanged"
          >
          </auto-complete-box>

          <auto-complete-box
            v-model="wound_treatment.dressing_used_2"
            :items="dressings.filter(x => x.title !== 'None')"
            :disabled="wound_treatment.dressing_used_1 === 'None'"
            label="Additional Dressing Used"
            class="col-sm-6"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.dressing_used_2 : null"
            :previous-value-dos="previousVisitDate"
          >
          </auto-complete-box>

          <text-area
            v-model="wound_treatment.dressing_used_other"
            :disabled="wound_treatment.dressing_used_1 === 'None'"
            :required="!isQuickEntry && this.$custom.isEmpty(wound_treatment.dressing_used_1)"
            class="col-sm-12"
            label="Enter other dressings used..."
            counter="1200"
            maxlength="1200"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.dressing_used_other : null"
            :previous-value-dos="previousVisitDate"
          ></text-area>

          <text-area
            v-if="isDakinDressing"
            v-model="wound_treatment.reason_for_dakins_solution"
            label="Enter justification for using Dakin’s solution and disclose the duration for which the patient will receive it..."
            class="col-sm-12"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.reason_for_dakins_solution : null"
            :previous-value-dos="previousVisitDate"
            required
          ></text-area>
        </NoteSection>

        <NoteSection
          label="Reason for Ongoing Debridement"
          :highlight-required="isReasonForOngoingDebridementIncomplete"
          :form-validated="formValidated"
        >
          <check-boxes
            v-model="computedReasonForOngoingDebridement"
            :items="reasonForOngoingDebridementDynamicList"
            class="col-sm-12 mb-2"
            :previous-value="hasPreviousWoundTreatment ? computedLatestTreatmentReasonForOngoingDebridement: null"
            :previous-value-dos="previousVisitDate"
            multiple
            columns="3"
            column-sort
          >
          </check-boxes>

          <text-area
            v-if="wound_treatment.ongoing_debridement_reason.includes('Other')"
            v-model="wound_treatment.ongoing_debridement_reason_other"
            label="Enter other reasons for ongoing debridement..."
            class="col-sm-12"
            :previous-value="hasPreviousWoundTreatment ? latestTreatment.wound_treatment.ongoing_debridement_reason_other : null"
            :previous-value-dos="previousVisitDate"
            required
            counter="1200"
            maxlength="1200"
          ></text-area>
        </NoteSection>

        <OperativeNote
          v-if="!isQuickEntry"
          v-model="treatment.operative_note"
          :operative-note-wizard.sync="treatment.operative_note_wizard"
          :wound-treatment.sync="wound_treatment"
          :treatments="wound.treatments"
          :treatment-id="treatment.id"
          :highlight-form="highlightForm"
          :patient="patient"
          :procedure-id="treatment.procedure_id"
          is-wound-treatment
          :required="debridementProcedurePerformed"
          :previous-op-note="latestTreatment && latestTreatment.operative_note ? latestTreatment.operative_note : null"
          :previous-dos="previousVisitDate"
          :wound-location="wound.location.location_text"
          :form-validated="formValidated"
        ></OperativeNote>

        <NoteSection
          v-if="isQuickEntry"
          label="Quick Entry"
          heading
        ></NoteSection>

        <QuickEntry
          v-if="isQuickEntry"
          :current-treatment.sync="treatment.current_treatment"
          :qent_notes.sync="treatment.qent_notes"
          :operative-note.sync="treatment.operative_note"
          :treatments="wound.treatments"
        ></QuickEntry>

        <v-card-actions>
          <!-- done -->
          <btn
            v-if="isSigned"
            label="Done"
            color="secondary"
            :icon="icons.mdiCheck"
            @click="done"
          ></btn>
          <!-- cancel -->
          <btn
            v-else
            label="Cancel"
            color="secondary"
            :icon="icons.mdiCancel"
            @click="cancel"
          ></btn>
          <!-- delete -->
          <btn
            v-if="isNewTreatment && !isSigned"
            label="Delete"
            color="error"
            :icon="icons.mdiTrashCan"
            rem:disabled="treatment.can_be_deleted !== true"
            @click="deleteTreatment"
          ></btn>

          <v-spacer></v-spacer>

          <!-- save draft -->
          <btn
            v-if="!isSigned"
            label="Save Draft"
            :icon="icons.mdiContentSaveEdit"
            color="success"
            @click="saveDraft"
          ></btn>

          <!-- validate/save -->
          <btn
            v-if="!isSigned"
            label="Validate / Save"
            :icon="icons.mdiCheckDecagram"
            @click="validateSave"
          ></btn>
        </v-card-actions>
      </v-form>
    </v-card>
    <v-dialog
      v-model="showAmnioErrors"
      width="900"
      persistent
      retain-focus
    >
      <v-card>
        <v-card-title
          class="text-h6"
          style="white-space: normal; word-break: normal; overflow-wrap: break-word;"
        >
          The following problems were found with the entries for this amniotic tissue graft:
        </v-card-title>
        <v-card-text>
          <alert
            v-if="!isTreatmentValidation"
            color="warning"
            dense
            :outlined="false"
            :border="'none'"
            :font-size="'14'"
            :icon-shape="'circle'"
            :text-color="'black'"
            rounded-border
            text
          >
            You may choose to ignore these for now, but the issues will need to be corrected upon completing the treatment.
          </alert>
          <ul>
            <li
              v-for="(message, index) in amnioInvalidMessages"
              :key="index"
            >
              {{ message }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="!isTreatmentValidation"
            color="secondary"
            @click="showAmnioErrors = false"
          >
            Ignore
          </v-btn>
          <v-btn
            color="primary"
            @click="() => { showAmnioErrors = false; openAmnio(); isTreatmentValidation = false; }"
          >
            Return
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import AttachmentModal from '@/components/notes/encounter/AttachmentModal.vue'
import PatientInformation from '@/components/notes/encounter/PatientInformation.vue'
import WoundNotHealingAttachments from '@/components/notes/encounter/WoundNotHealingAttachments.vue'
import AmnioticResearchInformation from '@/components/notes/wound-detail/treatments/sections/AmnioticRearchInformation.vue'
import AmnioticTissueGraft from '@/components/notes/wound-detail/treatments/sections/AmnioticTissueGraft.vue'
import ChangeLateralityModal from '@/components/notes/wound-detail/treatments/sections/ChangeLateralityModal.vue'
import Location from '@/components/notes/wound-detail/treatments/sections/Location.vue'
import OperativeNote from '@/components/notes/wound-detail/treatments/sections/OperativeNote.vue'
import QuickEntry from '@/components/notes/wound-detail/treatments/sections/QuickEntry.vue'
import WoundMeasurements from '@/components/notes/wound-detail/treatments/sections/WoundMeasurements.vue'
import WoundWheel from '@/components/notes/wound-detail/treatments/sections/WoundWheel.vue'
import ChangeLaterality from '@/mixins/ChangeLaterality'

// Best Guidelines
import { amnioReqData } from '@/store'
import BestPractice from '@/views/notes/best-practice/BestPractice.vue'
import {
  mdiBandage,
  mdiCancel,
  mdiCheck,
  mdiCheckDecagram,
  mdiClose,
  mdiContentSave,
  mdiContentSaveEdit,
  mdiFileDocument,
  mdiFileImage,
  mdiInformation,
  mdiInformationOutline,
  mdiTrashCan,
} from '@mdi/js'
import dayjs from 'dayjs'
import { mapGetters } from 'vuex'

const woundInitial = {
  wound_number: null,
  practice_type_id: null,
  is_wound_facility_acquired: false,
  is_wound_not_recent: false,
  wound_acquired_facility_id: null,
  amnio_pre_treatment_duration_weeks: null,
  location: {},

  // I believe this should only be at the treatment level, not the wound level
  // operative_note_wizard: {},
}
const inside = {
  inside_quadrant_1: null,
  inside_quadrant_2: null,
  inside_quadrant_3: null,
  inside_quadrant_4: null,
}
const outside = {
  outside_1_oclock: null,
  outside_2_oclock: null,
  outside_3_oclock: null,
  outside_4_oclock: null,
  outside_5_oclock: null,
  outside_6_oclock: null,
  outside_7_oclock: null,
  outside_8_oclock: null,
  outside_9_oclock: null,
  outside_10_oclock: null,
  outside_11_oclock: null,
  outside_12_oclock: null,
}
const woundTreatmentInitial = {
  wound_treatment_wound_size: {
    primary_length: null,
    primary_width: null,
    primary_depth: null,
    primary_area: null,
    slough: null,
    granulation: null,
    necrotic_tissue: null,
    hyper_granulation: null,
    eschar: null,
    epithelial: null,
    dermal: null,
    pre_debridement_total: null,
    unhealthy_granulation: false,
    wound_stage: null,
    post_debridement_length: null,
    post_debridement_width: null,
    post_debridement_depth: null,
    post_debridement_area: null,
    post_debridement_percent: null,
    post_debridement_area_debrided: null,
    wound_size_text: null,
  },
  progress: null,
  progress_other: null,
  odors: [],
  exudate_amounts: [],
  exudate_types: [],
  periwounds: [],
  wound_edges: [],
  infection_signs: [],
  wound_treatment_undermining: { ...inside, ...outside },
  wound_treatment_tunneling: { ...outside },
  wound_treatment_sinus_tract: { ...outside },
  pain_level: null,
  wound_description_summary: null,
  preoperative_indications: [],
  preoperative_indication_other: null,
  dressing_used_1: null,
  dressing_used_2: null,
  dressing_used_other: null,
  reason_for_dakins_solution: null,
  debridement_not_performed_reasons: [],
  debridement_not_performed_reason_other: null,
  ongoing_debridement_reason: [],
  ongoing_debridement_reason_other: null,
  current_treatment: null,
  qent_notes: null,
  operative_note_wizard: {},
  operative_note: null,
  amnio_grafts: [],
  wound_treatment_amnio_fixations: [],
  wound_treatments_amnios: {},
  wound_treatments_amnios_research: {},
  wound_treatments_amnios_researches_symptoms: [],
}

export default {
  components: {
    PatientInformation,
    AttachmentModal,
    Location,
    ChangeLateralityModal,
    WoundMeasurements,
    WoundWheel,
    OperativeNote,
    QuickEntry,
    BestPractice,
    AmnioticTissueGraft,
    AmnioticResearchInformation,
    WoundNotHealingAttachments,
  },
  mixins: [ChangeLaterality],
  props: {
    value: {
      type: Object,
      default: null,
    },
    modalState: {
      type: Boolean,
      default: false,
    },
    patient: {
      type: Object,
      default: null,
    },
    isSigned: {
      type: [Boolean, Number],
      default: null,
    },
    isCirculationCompromised: {
      type: Boolean,
      default: null,
    },
    encounterId: {
      type: String,
      default: null,
    },
    encounterVisitDate: {
      type: [String, Date],
      default: null,
    },
    encounterCreatedDate: {
      type: [String, Date],
      default: null,
    },
    visitLocation: {
      type: String,
      default: null,
    },
    quickEntry: {
      type: Boolean,
      default: null,
    },
    latestTreatment: {
      type: Object,
      default: null,
    },
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDomUpdated: false,
      formValid: false,
      highlightForm: false,
      attachmentModal: false,
      attachmentType: null,
      locationModal: false,
      amnioModal: false,
      researchModal: false,
      operativeNoteWizardModal: false,
      wheelSize: 300,
      wound: {},
      treatment: {},
      wound_treatment: {},
      painLevel: [
        { title: '0', id: 0 },
        { title: '1', id: 1 },
        { title: '2', id: 2 },
        { title: '3', id: 3 },
        { title: '4', id: 4 },
        { title: '5', id: 5 },
        { title: '6', id: 6 },
        { title: '7', id: 7 },
        { title: '8', id: 8 },
        { title: '9', id: 9 },
        { title: '10', id: 10 },
      ],
      researchTypes: ['control', 'test'],
      onLeg: [
        'ball of foot',
        'calf',
        'foot',
        'heel',
        'knee',
        'leg',
        'malleolus',
        'shin',
        'thigh',
        'tibia',
        'toe',
        'ankle',
      ],
      onFoot: ['ball of foot', 'foot', 'heel', 'toe'], // SWC-515: Ball of Foot, Foot
      onLowerLimb: ['leg', 'malleolus', 'foot', 'ball of foot', 'ankle'], // SWC-513: Leg, Malleolus, Foot, Ball of Foot
      onTorsoAndLimbs: ['leg', 'arm', 'chest', 'back', 'abdomen', 'shoulder'], // SWC-514: Leg, Arm, Chest, Back, Abdomen, Shoulder
      icons: {
        mdiCheck,
        mdiFileImage,
        mdiFileDocument,
        mdiCancel,
        mdiClose,
        mdiCheckDecagram,
        mdiContentSaveEdit,
        mdiContentSave,
        mdiTrashCan,
        mdiInformation,
        mdiBandage,
        mdiInformationOutline,
      },
      timeoutId: null,
      amnioValid: false,
      amnioResearchValid: false,
      isAmnioResearchTouched: false,
      isAmnioTouched: false,
      // Use provider's amniotic flag if acting as a scribe, otherwise use the logged-in user's setting
      isAmnioEnabled: this.$scribeProvider.id
        ? this.$scribeProvider.isAmnioticEnabled
        : this.$authUser.user().is_amniotic_enabled,
      showAmnioErrors: false,
      amnioInvalidMessages: '',
      closed: false,
      isTreatmentValidation: false,
      descriptionUpdated: false,
      wasAmnioTreated: false,
      allowedReasonsMap: {
        'arterial disease': [
          'Other',
          'Tobacco use (smoking)',
          'Nature of disease',
          'Progression of calcification',
          'Significant stenosis',
          'Infection being treated',
        ],
        'diabetes': [
          'Other',
          'Resistance to prescribed protective devices',
          'Tobacco use (smoking)',
          'Decreased sensation',
          'Repetitive Injury to ulcer',
          'Variance in blood sugar',
          'Difficulty offloading ulcer',
        ],
        'diabetes/pressure': [
          'Other',
          'Resistance to prescribed protective devices',
          'Tobacco use (smoking)',
          'Decreased sensation',
          'Repetitive Injury to ulcer',
          'Variance in blood sugar',
          'Difficulty offloading ulcer',
          'Nature of disease',
          'Multiple wounds',
          'Inability to offload a pressure site',
          'Recurring slough',
          'Excessive drainage',
          'Non-Compliance',
          'Severe contractures',
          'Difficulty eating',
          'Ongoing dialysis',
          'Ongoing infection',
          'Chronic osteomyelitis',
          'Poor appetite',
        ],
        'pad/pressure': [
          'Other',
          'Tobacco use (smoking)',
          'Nature of disease',
          'Progression of calcification',
          'Significant stenosis',
          'Infection being treated',
          'Resistance to prescribed protective devices',
          'Decreased sensation',
          'Repetitive Injury to ulcer',
          'Multiple wounds',
          'Inability to offload a pressure site',
          'Recurring slough',
          'Excessive drainage',
        ],
        'peripheral artery disease': [
          'Other',
          'Tobacco use (smoking)',
          'Nature of disease',
          'Progression of calcification',
          'Significant stenosis',
          'Infection being treated',
        ],
        'pressure': [
          'Other',
          'Resistance to prescribed protective devices',
          'Tobacco use (smoking)',
          'Decreased sensation',
          'Repetitive Injury to ulcer',
          'Variance in blood sugar',
          'Difficulty offloading ulcer',
          'Nature of disease',
          'Multiple wounds',
          'Inability to offload a pressure site',
          'Recurring slough',
          'Excessive drainage',
          'Non-Compliance',
          'Severe contractures',
          'Difficulty eating',
          'Ongoing dialysis',
          'Ongoing infection',
          'Chronic osteomyelitis',
          'Poor appetite',
        ],
        'pressure injury/ulcer': [
          'Other',
          'Tobacco use (smoking)',
          'Nature of disease',
          'Progression of calcification',
          'Significant stenosis',
          'Infection being treated',
          'Resistance to prescribed protective devices',
          'Decreased sensation',
          'Repetitive Injury to ulcer',
          'Variance in blood sugar',
          'Difficulty offloading ulcer',
          'Multiple wounds',
          'Inability to offload a pressure site',
          'Recurring slough',
          'Excessive drainage',
          'Non-Compliance',
          'Severe contractures',
          'Difficulty eating',
          'Ongoing dialysis',
          'Ongoing infection',
          'Chronic osteomyelitis',
          'Poor appetite',
        ],
        'venous disease': [
          'Other',
          'Severe venous hypertension',
          'Significant venous disease',
          'Deep Venous Thrombosis',
          'Venous skin',
          'Skin Hypersensitivity and breakdown',
          'Failure to comply with compression devices',
        ],
        'venous stasis disease': [
          'Other',
          'Recurring slough',
          'Excessive drainage',
          'Severe venous hypertension',
          'Significant venous disease',
          'Deep Venous Thrombosis',
          'Venous skin',
          'Skin Hypersensitivity and breakdown',
          'Failure to comply with compression devices',
        ],
      },
    }
  },
  beforeDestroy() {
    // Cleanup the timeout to avoid memory leaks
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
  },
  computed: {
    ...mapGetters('baseData', ['woundProcedures']),
    ...mapGetters('auth', ['user']),
    ...mapGetters('encounters', [
      'debridementNotPerformedReasons',
      'dressings',
      'woundEtiologies',
      'exudateAmounts',
      'exudateTypes',
      'infectionSigns',
      'odors',
      'periwounds',
      'preoperativeIndications',
      'progresses',
      'reasonForOngoingDebridement',
      'woundEdges',
    ]),
    debrNotPerformedValueList() {
      const list = this.debridementNotPerformedReasons
      return [
        ...list.filter(item => item.title !== 'Other'),
        ...list.filter(item => item.title === 'Other')
      ]
    },
    isSuperUser() {
      return this.$authUser.user().is_superuser
    },
    userId() {
      return this.$authUser.user().id
    },
    isLoaded() {
      return (this.wound && typeof this.wound.wound_number !== 'undefined')
    },
    isQuickEntry() {
      return (this.$route.query.quick || this.$route.query.quick === 'true') && this.quickEntry
    },
    isNewTreatment() {
      return this.encounterId === this.treatment.encounter_id
    },
    isEtiologyPressure() {
      return [
        this.treatment.etiology,
        this.treatment.etiology_other
      ].some(value => {
        if (!value) return false

        return /\bpressure\b/i.test(value)
          && !/\bnon-pressure\b/i.test(value)
      })
    },
    isEtiologyPadPvd() {
      const keywords = [
        'pad',
        'peripheral artery disease',
        'pvd',
        'peripheral vascular disease',
      ]

      return keywords.some(keyword => this.fieldContains(this.treatment.etiology, keyword)
        || this.fieldContains(this.treatment.etiology_other, keyword))
    },
    procedureTitle() {
      return this.$store.getters['baseData/procedureFromId'](this.treatment.procedure_id)
    },
    previousProcedureTitle() {
      if (this.latestTreatment && this.latestTreatment.procedure_id) return this.$store.getters['baseData/procedureFromId'](this.latestTreatment.procedure_id)

      return null
    },
    isOtherPreoperativeIndication() {
      return this.wound_treatment.preoperative_indications.some(x => x.title === 'Other')
    },
    isProcedureDebridement() {
      return this.fieldContains(this.procedureTitle, 'debrid')
    },
    isProcedureFoot() {
      return this.fieldContains(this.procedureTitle, 'foot')
    },
    isDakinDressing() {
      return this.fieldContains(this.wound_treatment.dressing_used_1, 'dakin')
        || this.fieldContains(this.wound_treatment.dressing_used_2, 'dakin')
        || this.fieldContains(this.wound_treatment.dressing_used_other, 'dakin')
    },
    isLocationOnLeg() {
      return this.onLeg.some(i => (this.wound.location?.location_text || '').toLowerCase().includes(i))
    },
    isLocationOnFoot() {
      return this.onFoot.some(i => (this.wound.location?.location_text || '').toLowerCase().includes(i))
    },
    isLowerLimb() {
      return this.onLowerLimb.some(i => (this.wound.location?.location_text || '').toLowerCase().includes(i))
    },
    isTorsoAndLimbs() {
      return this.onTorsoAndLimbs.some(i => (this.wound.location?.location_text || '').toLowerCase().includes(i))
    },
    imageAttachmentCount() {
      return (this.patient.all_attachments || []).filter(item =>
        item.attachment_type === 'images' &&
        item.wound_id === this.wound.id
      ).length
    },
    debridementProcedurePerformed() {
      const procedure = this.$store.getters['baseData/procedureFromId'](this.treatment.procedure_id)

      return procedure && procedure.toLowerCase().includes('debrid')
    },
    isProcedureAmnioticTissueGraft() {
      return this.treatment.procedure_id === 178
    },
    requireReasonForNoDebridement() {
      // Return true if procedure is none and slough + necrotic tissue
      // is greater than or equal to 30
      return this.treatment.procedure_id === 999
      && (
        Number(this.wound_treatment.wound_treatment_wound_size.slough || 0)
          + Number(this.wound_treatment.wound_treatment_wound_size.necrotic_tissue || 0)
          >= 30
      )
    },
    require_vitals() {
      return !this.wound_treatment.infection_signs.some(x => x.title === 'None')
    },
    computedReasonForOngoingDebridement: {
      get() {
        const raw = this.wound_treatment.ongoing_debridement_reason

        if (!raw) return []

        if (Array.isArray(raw)) return raw

        return raw.split(',').map(s => s.trim())
      },
      set(newValue) {
        const val = Array.isArray(newValue) ? newValue : []
        this.wound_treatment.ongoing_debridement_reason = val

        const hasOther = val.some(v => v.toLowerCase() === 'other')
        if (!hasOther) {
          this.wound_treatment.ongoing_debridement_reason_other = null
        }
      },
    },
    computedLatestTreatmentReasonForOngoingDebridement() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.ongoing_debridement_reason) {
        return null
      }

      const reasons = Array.isArray(treatment.ongoing_debridement_reason)
        ? treatment.ongoing_debridement_reason
        : treatment.ongoing_debridement_reason.split(',')

      // Other get's replaced with the input from ongoing_debridement_reason_other
      const formattedReasons = reasons.map(reason => (reason.trim() === 'Other' ? treatment.ongoing_debridement_reason_other : reason.trim()))

      return formattedReasons.join(', ')
    },
    previousVisitDate() {
      return this.latestTreatment && this.latestTreatment.encounter && this.latestTreatment.encounter.visit_date
        ? this.latestTreatment.encounter.visit_date : null
    },
    hasPreviousWoundTreatment() {
      return this.latestTreatment && this.latestTreatment.wound_treatment
    },
    previousWoundProgress() {
      if (this.latestTreatment?.wound_treatment?.progress?.includes('Other')) return `Other: ${this.latestTreatment.wound_treatment.progress_other}`

      return this.latestTreatment.wound_treatment.progress
    },
    previousEtiology() {
      if (this.latestTreatment?.etiology?.includes('Other')) return `Other: ${this.latestTreatment.etiology_other}`

      return this.latestTreatment.etiology
    },
    previousPreoperativeIndications() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.preoperative_indications) {
        return null
      }
      const formattedPreoperativeIndications = treatment?.preoperative_indications?.map(preoperative_indication => (preoperative_indication.title.includes('Other') ? treatment.preoperative_indication_other : preoperative_indication.title))

      return formattedPreoperativeIndications.join(', ')
    },
    previousDebridementNotPerformedReasons() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.debridement_not_performed_reasons) return null

      const formattedPreoperativeIndications = treatment?.debridement_not_performed_reasons?.map(debr_not_performed_reason => (debr_not_performed_reason.title.includes('Other') ? treatment.debridement_not_performed_reason_other : debr_not_performed_reason.title))

      return formattedPreoperativeIndications.join(', ')
    },
    previousInfectionSigns() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.infection_signs) return null

      return treatment?.infection_signs?.map(infSign => infSign.title).join(', ')
    },
    previousWoundEdges() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.wound_edges) return null

      return treatment?.wound_edges?.map(woundEdge => woundEdge.title).join(', ')
    },
    previousPeriwounds() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.periwounds) return null

      return treatment?.periwounds?.map(periwound => periwound.title).join(', ')
    },
    previousExudateTypesAndAmount() {
      const treatment = this.latestTreatment?.wound_treatment
      let message = ''

      if (!treatment) return null

      if (treatment?.exudate_amounts && treatment?.exudate_amounts.length > 0) message += `Amount: ${treatment?.exudate_amounts?.map(exAmount => exAmount.title).join(', ')}\n`
      if (treatment?.exudate_types && treatment?.exudate_types.length > 0) message += `Types: ${treatment?.exudate_types?.map(exType => exType.title).join(', ')}`

      return message || null
    },
    previousOdors() {
      const treatment = this.latestTreatment?.wound_treatment

      if (!treatment || !treatment.odors) return null

      return treatment?.odors?.map(odor => odor.title).join(', ')
    },
    selectedExudateAmounts: {
      get() {
        if (this.wound_treatment.exudate_amounts && !Array.isArray(this.wound_treatment.exudate_amounts)) return [this.wound_treatment.exudate_amounts]
        if (!this.wound_treatment.exudate_amounts) return []

        return this.wound_treatment.exudate_amounts
      },
      set(value) {
        this.wound_treatment.exudate_amounts = value
      },
    },
    amnioBtnColor() {
      if (!this.isAmnioTouched) return 'primary'

      return this.amnioValid ? 'success' : 'error'
    },
    amnioResearchBtnColor() {
      if (!this.isAmnioResearchTouched) return 'primary'

      return this.amnioResearchValid ? 'success' : 'error'
    },
    isControlResearchType() {
      return this.patient.amnio_research_patient_type === 'control'
    },
    isTestResearchType() {
      return this.patient.amnio_research_patient_type === 'test'
    },

    // Dynamically set the values based on etiology or other conditions
    reasonForOngoingDebridementDynamicList() {
      const etiology = (this.treatment.etiology === 'Other'
        ? this.treatment.etiology_other
        : this.treatment.etiology) || ''

      const allowedReasons = (
        this.allowedReasonsMap[etiology.toLowerCase()] || ['Other']
      ).map(r => r.toLowerCase())

      return this.reasonForOngoingDebridement.filter(r =>
        allowedReasons.includes(r.title.toLowerCase())
      )
    },
    isWoundDescriptionAIDisabled() {
      // Helper function that checks if an object has any meaningful values
      const hasAnyData = (obj, ignoreKeys = []) => {
        if (!obj) return false

        return Object.entries(obj)
          .filter(([key]) => !ignoreKeys.includes(key)) // ignore system fields
          .some(([, val]) => val != null && val !== '') // check meaningful values
      }

      const ignoreFields = ['treatment_id', 'created', 'modified', 'id_sync']

      const wt = this.treatment?.wound_treatment

      const hasUndermining = hasAnyData(
        wt?.wound_treatment_undermining,
        ignoreFields
      )

      const hasTunneling = hasAnyData(
        wt?.wound_treatment_tunneling,
        ignoreFields
      )

      const hasSinusTract = hasAnyData(
        wt?.wound_treatment_sinus_tract,
        ignoreFields
      )

      return !this.treatment.wound_treatment.wound_treatment_wound_size.unhealthy_granulation
        && (this.selectedExudateAmounts.length === 0 && this.treatment.wound_treatment.exudate_types.length === 0)
        && (this.treatment.wound_treatment.pain_level === null || this.treatment.wound_treatment.pain_level === undefined || this.treatment.wound_treatment.pain_level === '')
        && this.treatment.wound_treatment.wound_edges.length === 0
        && this.treatment.wound_treatment.periwounds.length === 0
        && this.treatment.wound_treatment.odors.length === 0
        && !hasSinusTract
        && !hasTunneling
        && !hasUndermining
    },
    isSignsOfInfectionError() {
      const v = this.wound_treatment.infection_signs
      return !v || v.length === 0
    },
    isPainLevelError() {
      const v = this.wound_treatment.pain_level
      return v === null || v === undefined || v === ''
    },
    isPreopIndicationError() {
      const v = this.wound_treatment.preoperative_indications
      return !v || v.length === 0
    },

    // Evaluates if Location & Etiology section is incomplete
    isLocationEtiologyIncomplete() {
      const hasLocation = !!this.wound?.location?.location_text
      const hasEtiology = !!this.treatment?.etiology
      const hasEtiologyOther = this.treatment?.etiology === 'Other'
        ? !!(this.treatment?.etiology_other && this.treatment.etiology_other.trim())
        : true

      return !hasLocation || !hasEtiology || !hasEtiologyOther
    },
    // Evaluates if Procedure Performed section is incomplete
    isProcedureIncomplete() {
      return !this.treatment?.procedure_id
    },
    // Evaluates if Signs of Infection section is incomplete
    isSignsOfInfectionIncomplete() {
      return this.isSignsOfInfectionError
    },
    // Evaluates if Preoperative Indications section is incomplete
    isPreopIndicationsIncomplete() {
      if (!this.debridementProcedurePerformed) return false
      if (this.isPreopIndicationError) return true
      if (this.isOtherPreoperativeIndication && (!this.wound_treatment.preoperative_indication_other || !this.wound_treatment.preoperative_indication_other.trim())) return true

      return false
    },
    // Evaluates if Dressings Used section is incomplete
    isDressingsUsedIncomplete() {
      if (this.isQuickEntry) return false
      const hasDressing1 = !!this.wound_treatment?.dressing_used_1
      const hasDressingOther = !!(this.wound_treatment?.dressing_used_other && this.wound_treatment.dressing_used_other.trim())

      if (!hasDressing1 && !hasDressingOther) return true
      if (this.isDakinDressing && (!this.wound_treatment?.reason_for_dakins_solution || !this.wound_treatment.reason_for_dakins_solution.trim())) return true

      return false
    },
    // Evaluates if Reason for Ongoing Debridement requires input
    isReasonForOngoingDebridementIncomplete() {
      const reasons = this.computedReasonForOngoingDebridement || []
      const hasOther = Array.isArray(reasons)
        ? reasons.some(r => (typeof r === 'string' ? r : r?.title) === 'Other')
        : false

      if (hasOther) {
        const hasOtherText = typeof this.wound_treatment?.ongoing_debridement_reason_other === 'string'
          && this.wound_treatment.ongoing_debridement_reason_other.trim().length > 0

        return !hasOtherText
      }

      return false
    },
    // Evaluates if Exudate section is incomplete (required during Amniotic Tissue Graft)
    isExudateIncomplete() {
      if (!this.isProcedureAmnioticTissueGraft) return false

      const hasAmount = Array.isArray(this.selectedExudateAmounts) && this.selectedExudateAmounts.length > 0
      const isNoneSelected = this.selectedExudateAmounts.some(x => x && x.title === 'None')
      const hasType = Array.isArray(this.wound_treatment?.exudate_types) && this.wound_treatment.exudate_types.length > 0

      if (!hasAmount) return true
      if (!isNoneSelected && !hasType) return true

      return false
    },
    // Evaluates if Pain Level section is incomplete (required during Amniotic Tissue Graft)
    isPainLevelIncomplete() {
      if (!this.isProcedureAmnioticTissueGraft) return false

      return this.isPainLevelError
    },
    // Evaluates if Reasons for Debridement Not Performed section is incomplete
    isReasonsForNoDebridementIncomplete() {
      if (!this.requireReasonForNoDebridement) return false

      const reasons = this.wound_treatment?.debridement_not_performed_reasons
      if (!reasons || !Array.isArray(reasons) || reasons.length === 0) return true

      const hasOther = reasons.some(x => x?.title === 'Other')
      if (hasOther && (!this.wound_treatment?.debridement_not_performed_reason_other || !this.wound_treatment.debridement_not_performed_reason_other.trim())) {
        return true
      }

      return false
    },
  },
  watch: {
    modalState() {
      this.highlightForm = false
      if (!this.modalState) {
        // Reset wound and treatment objects
        this.wound = {}
        this.treatment = {}

        // Always set this property back to false after closing the modal
        // to prevent certain field watchers from running unintentionally
        // next time the user opens the wound detail modal.
        this.isDomUpdated = false

        // Scroll to top of modal
        const modalTitle = this.$refs.woundTitle
        if (modalTitle) {
          modalTitle.scrollIntoView(true)
        }
      } else {
        // Simulate data loading, use this property to prevent some watchers from
        // running unintentionally when the woundDetail modal is opened.
        if (this.timeoutId) {
          clearTimeout(this.timeoutId) // Clear any existing timeout
        }

        this.timeoutId = setTimeout(() => {
          this.isDomUpdated = true
        }, 1000)

        this.checkPreviousAmnioTreatment()
      }
    },
    value() {
      if (this.modalState && this.value && this.value.treatments) {
        const treatmentIndex = this.value.treatments.findIndex(x => x.encounter_id === this.encounterId && !x.deleted)
        this.wound = {
          ...this.$lodash.cloneDeep(woundInitial),
          ...this.$lodash.cloneDeep(this.value),
          treatments: Array.from(this.value.treatments),
        }
        if (!this.wound.location) this.wound.location = {}

        if (!this.treatment.operative_note_wizard) this.treatment.operative_note_wizard = {}

        const baseTreatment = this.$lodash.cloneDeep(this.value.treatments[treatmentIndex]) || {}

        this.wound.treatments[treatmentIndex] = {
          ...baseTreatment,
          etiology: baseTreatment.etiology ?? null,
          etiology_other: baseTreatment.etiology_other ?? null,
          wound_treatment: {
            ...this.$lodash.cloneDeep(woundTreatmentInitial),
            ...(baseTreatment.wound_treatment && this.$lodash.cloneDeep(baseTreatment.wound_treatment)),
          },
        }

        // Telemedicine can't have a treatment, so it must be [999] None.
        if (this.visitLocation === 'Telemedicine') {
          this.wound.treatments[treatmentIndex].procedure_id = 999

          // Also, clear the op note text.
          this.wound.treatments[treatmentIndex].operative_note = null
        }

        // Short references
        this.treatment = this.wound.treatments[treatmentIndex]
        this.wound_treatment = this.wound.treatments[treatmentIndex].wound_treatment

        this.$nextTick(() => {
          this.appendQentDataToSummary()
        })
      }
    },
    'wound_treatment.wound_treatment_undermining': {
      deep: true,
      handler() {
        if (this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.wound_treatment_tunneling': {
      deep: true,
      handler() {
        if (this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.wound_treatment_sinus_tract': {
      deep: true,
      handler() {
        if (this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.odors': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.exudate_amounts': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.exudate_types': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.periwounds': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.wound_edges': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.pain_level': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },
    'wound_treatment.wound_treatment_wound_size.unhealthy_granulation': {
      handler(curVal, oldVal) {
        if (curVal !== oldVal && this.isDomUpdated) this.descriptionUpdated = true
      },
    },

    // Clear reasons for no debridement if applicable --> procedure not set to none, sum of slough and necrotic tissue is less than 30).
    'treatment.procedure_id': {
      handler(newValue) {
        if (newValue !== 999) {
          this.clearReasonsForNoDebridement()
        }
        if (newValue === 178) { // Amnio procedure
          if (this.isControlResearchType) {
            this.controlSelectedAlert()
          }
          if (!this.isAmnioEnabled) {
            this.disabledAmnioUserDialog()
          }
        }
      },
    },
    'patient.amnio_research_patient_type': {
      handler(val) {
        if (val === 'control' && this.isProcedureAmnioticTissueGraft) {
          this.controlSelectedAlert()
          this.treatment.procedure_id = null
        }
      },
    },

    'wound_treatment.wound_treatment_wound_size.slough': {
      handler(newValue) {
        if (Number(newValue || 0)
        + Number(this.wound_treatment.wound_treatment_wound_size.necrotic_tissue || 0)
        < 30) {
          this.clearReasonsForNoDebridement()
        }
      },
    },
    'wound_treatment.wound_treatment_wound_size.necrotic_tissue': {
      handler(newValue) {
        if (Number(newValue || 0)
          + Number(this.wound_treatment.wound_treatment_wound_size.slough || 0)
          < 30) {
          this.clearReasonsForNoDebridement()
        }
      },
    },
    async closed(isClosed) {
      if (isClosed) {
        this.amnioInvalidMessages = this.$refs.amnioComponent?.getInvalidFields?.()
        if (!this.$custom.isObjectEmpty(this.amnioInvalidMessages)) this.showAmnioErrors = true
      }
    },
    'wound_treatment.wound_description_summary': {
      handler(newVal, oldVal) {
        if (newVal != oldVal && this.isDomUpdated) {
          this.descriptionUpdated = false
          if (this.modalState) {
            this.wound_treatment.is_custom_text_on_wound_desc = true
          }
        }
      }
    },
  },
  updated() {
    // Disabling this as it's no longer necessary since we're no longer showing the loading modal when opening the wound details.
    // It's also causing a huge delay for every input when there's extensive data in the store.
    // this.$store.dispatch('loading', false)
  },
  methods: {
    appendQentDataToSummary() {
      // Only process if is_custom_text_on_wound_desc is false
      if (this.wound_treatment.is_custom_text_on_wound_desc) {
        return
      }

      let summary = this.wound_treatment.wound_description_summary || ''
      let hasChanges = false

      // Extract different parts
      const underminingPattern = /Undermining comments:.*$/m
      const exudatePattern = /EXUDATE:[^\n]*/i

      let qentManualText = summary
      let underminingText = ''
      let exudateText = ''

      if (underminingPattern.test(summary)) {
        const match = summary.match(underminingPattern)
        underminingText = match[0]
        qentManualText = qentManualText.replace(underminingPattern, '').trim()
      }

      if (exudatePattern.test(qentManualText)) {
        const match = qentManualText.match(exudatePattern)
        exudateText = match[0]
        qentManualText = qentManualText.replace(exudatePattern, '').trim()
      }

      if (this.wound_treatment.qent_undermining) {
        underminingText = `Undermining comments: ${this.wound_treatment.qent_undermining}`
        hasChanges = true
      }

      const exudateAmounts = this.wound_treatment.exudate_amounts || []
      const exudateTypes = this.wound_treatment.exudate_types || []

      if (exudateAmounts.length > 0 || exudateTypes.length > 0) {
        exudateText = `EXUDATE: ${this.$custom.toList([...exudateAmounts, ...exudateTypes])}`
        hasChanges = true
      } else {
        exudateText = ''
        if (exudatePattern.test(summary)) hasChanges = true
      }

      let newSummary = ''

      if (qentManualText) {
        newSummary = qentManualText
      }

      if (underminingText) {
        newSummary += (newSummary ? '\n' : '') + underminingText
      }

      if (exudateText) {
        newSummary += (newSummary ? '\n' : '') + exudateText
      }

      if (hasChanges || newSummary !== summary) {
        this.wound_treatment.wound_description_summary = newSummary.trim()
        this.wound_treatment.is_custom_text_on_wound_desc = true
      }
    },
    async checkPreviousAmnioTreatment() {
      const prevTreatments = await amnioReqData.getItem(this.value.id)

      if (Array.isArray(prevTreatments)) {
        const encounterDos = dayjs(this.encounterVisitDate)
          .startOf('day')

        // Filter by date before current encounter's dos
        const filtered = prevTreatments.filter(item => {
          const visitDate = dayjs(item.visit_date)
            .startOf('day')

          return visitDate.isBefore(encounterDos)
        })

        this.wasAmnioTreated = filtered.some(item => item.procedure_id === 178)
      }
    },
    facilityAcquired(newState) {
      this.wound.is_wound_facility_acquired = newState
      if (newState) {
        this.$root.confirm({
          html: true,
          title: 'Report wound was acquired at facility?',
          body: `Would you like to report that this wound was acquired at <b>${this.$store.getters['facilities/getById'](this.treatment.place_of_service_id).title}</b>?`,
          confirm: 'Yes',
        }).then(result => {
          if (result) {
            this.wound.wound_acquired_facility_id = this.treatment.place_of_service_id
            this.$root.confirm({
              title: 'Wound not recent?',
              body: "Was this wound acquired prior to SWC's initial visit of the patient?",
              cancel: 'No',
              confirm: 'Yes',
            }).then(confirm => {
              this.wound.is_wound_not_recent = confirm
            })
          } else {
            this.$nextTick(() => {
              this.wound.is_wound_facility_acquired = false
              this.wound.is_wound_not_recent = false
              this.wound.wound_acquired_facility_id = null
            })
          }
        })
      } else {
        this.$root.confirm({
          title: 'Are you sure?',
          body: 'Are you sure you want to unmark this wound as facility-acquired?',
          confirm: 'Yes',
        }).then(result => {
          if (result) {
            this.wound.is_wound_not_recent = false
            this.wound.wound_acquired_facility_id = null
          } else {
            this.$nextTick(() => {
              this.wound.is_wound_facility_acquired = true
            })
          }
        })
      }
    },
    async generateWoundDescription(emptyWarning = true) {
      if (!this.modalState || !this.isDomUpdated) return

      let action = false

      if (this.wound_treatment.wound_description_summary && !this.hasOverwritten) {
        action = await this.$root.confirm({
          title: 'Custom Input Detected in Wound Description',
          body: 'Do you want to overwrite or append to your current input in the Wound Description field?',
          cancel: 'Cancel',
          middle: 'Overwrite',
          middleColor: 'warning',
          confirm: 'Append',
          confirmColor: 'success',
        })

        if (!action) return
      }

      let woundDescription = ''

      const orderByPropertyWithQuadrant = [
        'inside_quadrant_1',
        'outside_1_oclock',
        'outside_2_oclock',
        'outside_3_oclock',
        'inside_quadrant_2',
        'outside_4_oclock',
        'outside_5_oclock',
        'outside_6_oclock',
        'inside_quadrant_3',
        'outside_7_oclock',
        'outside_8_oclock',
        'outside_9_oclock',
        'inside_quadrant_4',
        'outside_10_oclock',
        'outside_11_oclock',
        'outside_12_oclock',
      ]
      const orderByProperty = [
        'outside_1_oclock',
        'outside_2_oclock',
        'outside_3_oclock',
        'outside_4_oclock',
        'outside_5_oclock',
        'outside_6_oclock',
        'outside_7_oclock',
        'outside_8_oclock',
        'outside_9_oclock',
        'outside_10_oclock',
        'outside_11_oclock',
        'outside_12_oclock',
      ]
      const definitions = {
        inside_quadrant_1: ' cm from 12 o\'clock to 3 o\'clock',
        inside_quadrant_2: ' cm from 3 o\'clock to 6 o\'clock',
        inside_quadrant_3: ' cm from 6 o\'clock to 9 o\'clock',
        inside_quadrant_4: ' cm from 9 o\'clock to 12 o\'clock',
        outside_1_oclock: ' cm at 1 o\'clock',
        outside_2_oclock: ' cm at 2 o\'clock',
        outside_3_oclock: ' cm at 3 o\'clock',
        outside_4_oclock: ' cm at 4 o\'clock',
        outside_5_oclock: ' cm at 5 o\'clock',
        outside_6_oclock: ' cm at 6 o\'clock',
        outside_7_oclock: ' cm at 7 o\'clock',
        outside_8_oclock: ' cm at 8 o\'clock',
        outside_9_oclock: ' cm at 9 o\'clock',
        outside_10_oclock: ' cm at 10 o\'clock',
        outside_11_oclock: ' cm at 11 o\'clock',
        outside_12_oclock: ' cm at 12 o\'clock',
      }

      const newSections = {}

      let temp = ''
      orderByPropertyWithQuadrant.forEach(property => {
        const woundTreatmentUndermining = this.treatment.wound_treatment.wound_treatment_undermining[property]
        if (woundTreatmentUndermining !== null && woundTreatmentUndermining !== undefined && woundTreatmentUndermining !== '') {
          temp += `\t${woundTreatmentUndermining + definitions[property]}\n`
        }
      })
      if (temp !== '') {
        newSections.UNDERMINING = `UNDERMINING:\n${temp}`
      }

      temp = ''
      orderByProperty.forEach(property => {
        const woundTreatmentTunneling = this.treatment.wound_treatment.wound_treatment_tunneling[property]
        if (woundTreatmentTunneling !== null && woundTreatmentTunneling !== undefined && woundTreatmentTunneling !== '') {
          temp += `\t${woundTreatmentTunneling + definitions[property]}\n`
        }
      })
      if (temp !== '') {
        newSections.TUNNELLING = `TUNNELLING:\n${temp}`
      }

      temp = ''
      orderByProperty.forEach(property => {
        const woundTreatmentSinusTract = this.treatment.wound_treatment.wound_treatment_sinus_tract[property]
        if (woundTreatmentSinusTract !== null && woundTreatmentSinusTract !== undefined && woundTreatmentSinusTract !== '') {
          temp += `\t${woundTreatmentSinusTract + definitions[property]}\n`
        }
      })
      if (temp !== '') {
        newSections['SINUS TRACT'] = `SINUS TRACT:\n${temp}`
      }

      if (this.treatment.wound_treatment.odors.length > 0) {
        newSections.ODOR = `ODOR: ${this.$custom.toList(this.treatment.wound_treatment.odors)}\n`
      }
      if (this.treatment.wound_treatment.periwounds.length > 0) {
        newSections.PERIWOUND = `PERIWOUND: ${this.$custom.toList(this.treatment.wound_treatment.periwounds)}\n`
      }
      if (this.treatment.wound_treatment.wound_edges.length > 0) {
        newSections['WOUND EDGE'] = `WOUND EDGE: ${this.$custom.toList(this.treatment.wound_treatment.wound_edges)}\n`
      }
      if (this.treatment.wound_treatment.pain_level !== null && this.treatment.wound_treatment.pain_level !== undefined && this.treatment.wound_treatment.pain_level !== '') {
        newSections.PAIN = `PAIN: ${this.treatment.wound_treatment.pain_level}/10\n`
      }
      if (Array.isArray(this.selectedExudateAmounts) && (this.selectedExudateAmounts.length > 0 || this.treatment.wound_treatment.exudate_types.length > 0)) {
        newSections.EXUDATE = `EXUDATE: ${this.$custom.toList([...this.selectedExudateAmounts, ...this.treatment.wound_treatment.exudate_types])}\n`
      }
      if (this.treatment.wound_treatment.wound_treatment_wound_size.unhealthy_granulation) {
        newSections.UNHEALTHY_GRANULATION = 'Unhealthy granulation tissue has been identified within the wound.\n'
      }

      woundDescription = Object.values(newSections).join('')

      if (action == true) {
        const existing = this.treatment.wound_treatment.wound_description_summary || ''

        // Split into user part and auto part (double newline separates sections)
        let parts = existing.split(/\n{2,}/)
        let userPart = parts[0] ? parts[0].trim() : ''
        let autoPart = parts[1] ? parts.slice(1).join('\n\n').trim() : ''

        const sectionPatterns = {
          UNDERMINING: /UNDERMINING:[\s\S]*?(?=\n[A-Z][A-Z ]+:|$)/i,
          TUNNELLING: /TUNNELLING:[\s\S]*?(?=\n[A-Z][A-Z ]+:|$)/i,
          'SINUS TRACT': /SINUS TRACT:[\s\S]*?(?=\n[A-Z][A-Z ]+:|$)/i,
          ODOR: /ODOR:[^\n]*/i,
          PERIWOUND: /PERIWOUND:[^\n]*/i,
          'WOUND EDGE': /WOUND EDGE:[^\n]*/i,
          PAIN: /PAIN:[^\n]*/i,
          EXUDATE: /EXUDATE:[^\n]*/i,
          UNHEALTHY_GRANULATION: /Unhealthy granulation tissue has been identified[^\n]*/i,
        }

        // Start with combined text: just append autoPart directly after userPart
        let combined = userPart
        if (autoPart) combined += (combined ? '\n' : '') + autoPart

        // Replace or append each section
        Object.keys(newSections).forEach(sectionKey => {
          const newSec = newSections[sectionKey]?.trim()
          if (!newSec) return

          const regex = sectionPatterns[sectionKey]

          if (regex.test(combined)) {
            combined = combined.replace(regex, newSec)
          } else {
            combined = (combined ? combined + '\n' : '') + newSec
          }
        })

        // Clean final text
        combined = this.$custom.clean(combined, 'Wound Description', emptyWarning) || ''

        // Save result
        this.treatment.wound_treatment.wound_description_summary = combined
        this.descriptionUpdated = false
      } else if (Object.keys(newSections).length === 0) {
        return
      } else {
        this.treatment.wound_treatment.wound_description_summary = this.$custom.clean(woundDescription, 'Wound Description', emptyWarning)
      }

      this.descriptionUpdated = false
    },
    clearLocation() {
      this.wound.location = { wound_id: this.wound.id }
    },
    fieldContains(field, contains) {
      return !field ? false : field.toLowerCase().includes(contains)
    },
    etiologyChanged(newEtiology) {
      this.etiologyLogic(this.treatment.etiology, newEtiology, false)
    },
    etiologyOtherChanged(event) {
      const newValue = (event?.target?.value || '').trim()

      if (this.treatment.etiology_other !== newValue) {
        this.etiologyLogic(this.treatment.etiology_other, newValue, true)
      }
    },
    etiologySetNew(newEtiology, isOther) {
      if (isOther) {
        this.treatment.etiology_other = newEtiology
      } else {
        this.treatment.etiology = newEtiology
        if (this.treatment.etiology !== 'Other') {
          this.treatment.etiology_other = null
        }
      }
    },
    etiologyReset(currentEtiology, isOther) {
      if (isOther) {
        // This is done to make sure we change it to a different value so the field updates
        this.treatment.etiology_other = (currentEtiology === null ? '' : null)
        this.$nextTick(() => {
          this.treatment.etiology_other = currentEtiology
        })
      } else {
        this.treatment.etiology = null
        this.$nextTick(() => {
          this.treatment.etiology = currentEtiology
          if (this.treatment.etiology !== 'Other') {
            this.treatment.etiology_other = null
          }
        })
      }
    },
    etiologyLogic(currentEtiology, newEtiology, isOther) {
      let defaultAction = true

      // Wound etiology is not allowed to contain the substring 'resolv'
      if (this.fieldContains(newEtiology, 'resolv')) {
        this.etiologyReset(currentEtiology, isOther)
        defaultAction = false
        this.$store.dispatch('notify', { value: 'Mention of wound resolution is not allowed in the wound etiology.', color: 'error' })
      }

      // Previously 'pressure' etiology, no longer pressure, so clear wound_stage and notify user
      if (this.isEtiologyPressure
        && this.wound_treatment.wound_treatment_wound_size.wound_stage
        && this.isEtiologyPressure !== this.fieldContains(newEtiology, 'pressure')
      ) {
        this.wound_treatment.wound_treatment_wound_size.wound_stage = null
        this.$store.dispatch('notify', { value: 'Wound staging cleared as it is no longer categorized as a pressure injury.', color: 'warning' })
      }

      // Change to Etiology causes ongoing_debridement_reason to be cleared
      if (
        Array.isArray(this.wound_treatment.ongoing_debridement_reason)
          ? this.wound_treatment.ongoing_debridement_reason.length > 0
          : !!this.wound_treatment.ongoing_debridement_reason
      ) {
        defaultAction = false // Because the action is based on the confirmation selected
        this.$root.confirm({
          title: 'Are you sure you wish to change etiology?',
          body: 'Changing the etiology will reset your reasons for ongoing debridement.',
        }).then(result => {
          if (result) {
            this.etiologySetNew(newEtiology, isOther)
            this.wound_treatment.ongoing_debridement_reason = []
            this.wound_treatment.ongoing_debridement_reason_other = null
          } else {
            this.etiologyReset(currentEtiology, isOther)
          }
        })
      }

      // No special conditions, so change to the new Etiology
      if (defaultAction) {
        this.etiologySetNew(newEtiology, isOther)
        this.wound_treatment.ongoing_debridement_reason = []
        this.wound_treatment.ongoing_debridement_reason_other = null
      }

      if (this.isProcedureAmnioticTissueGraft
        && !this.isEtiologyPadPvd
        && this.treatment?.wound_treatment?.wound_treatments_amnios?.is_adequate_circulation
      ) {
        this.treatment.wound_treatment.wound_treatments_amnios.is_adequate_circulation = false
      }
    },
    firstDressingUsedChanged() {
      if (this.wound_treatment.dressing_used_1 === 'None') {
        this.wound_treatment.dressing_used_2 = null
        this.wound_treatment.dressing_used_other = null
      }
    },
    attachmentTypeClicked() {
      this.attachmentType = 'images'
      this.attachmentModal = true
    },
    exudateAmountChanged() {
      if (this.selectedExudateAmounts?.some(x => x && x.title === 'None')) {
        this.wound_treatment.exudate_types = []
      }
    },
    operativeNoteWizardClicked() {
      this.operativeNoteWizardModal = true
    },
    done() {
      this.$emit('update:modal-state', false)
    },
    cancel() {
      this.$root.confirm({
        title: 'Discard Treatment Changes?',
        body: 'Are you sure you wish to discard treatment changes?',
        cancel: 'No',
        confirm: 'Discard',
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.isAmnioTouched = false
          this.isAmnioResearchTouched = false
          this.done()
        }
      })
    },
    deleteTreatment() {
      this.$root.confirm({
        title: 'Delete Treatment Record?',
        body: 'Are you sure you wish to delete this treatment record?',
        alert: true,
        alertText: 'This operation cannot be undone!',
        alertColor: 'error',
        alertWarning: true,
        confirm: 'Delete',
        confirmIcon: this.icons.mdiTrashCan,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          const treatmentId = this.treatment.id
          if (this.treatment.can_be_deleted) {
            const treatmentIndex = this.wound.treatments.findIndex(x => x.id === this.treatment.id)
            this.wound.treatments.splice(treatmentIndex, 1)
            this.treatment = {}
            if (this.wound.treatments.length === 0 && this.wound.can_be_deleted) this.wound.delete_wound = true
          } else {
            this.wound.updated = true
            this.treatment.updated = true
            this.treatment.deleted = true
          }

          // Delete treatment attachments
          this.$store.commit('patients/deleteAttachmentsByTreatment', {
            id: this.patient.id,
            treatmentId: treatmentId,
          })
          this.patient.all_attachments = this.patient.all_attachments.filter(a => a.treatment_id !== treatmentId)

          this.isAmnioTouched = false
          this.isAmnioResearchTouched = false
          this.$emit('input', this.wound)
          this.done()
        }
      })
    },
    saveDraft() {
      this.treatment.updated = true
      this.wound.updated = true
      this.treatment.is_validated = false
      this.treatment.wound_treatment.ongoing_debridement_reason = this.treatment.wound_treatment.ongoing_debridement_reason.toString()
      this.treatment.require_vitals = this.require_vitals
      this.treatment.modified = this.$custom.utcNow()
      this.wound.modified = this.$custom.utcNow()
      this.isAmnioTouched = false
      this.isAmnioResearchTouched = false
      this.$emit('input', this.wound)
      this.done()
    },
    getPostDebridementAreaDebrided() {
      if (!this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length
        || !this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width
        || !this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_percent
      ) return null

      return (
        (this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length
          * this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width
          * this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_percent
        ) / 100).toFixed(2)
    },
    getPostDebridementArea() {
      if (!this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length
        || !this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width
      ) return null

      return (this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length
       * this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width).toFixed(2)
    },
    async validateAmnio() {
      this.amnioModal = true
      await this.$nextTick()
      this.amnioValid = await this.$refs.amnioComponent?.validate?.()
      this.amnioModal = false
    },
    async validateResearchAmnio() {
      this.researchModal = true
      await this.$nextTick()
      this.amnioResearchValid = await this.$refs.researchComponent?.validateForm?.()
      this.researchModal = false
    },
    async validateSave() {
      this.isAmnioTouched = true
      this.isAmnioResearchTouched = true
      await this.validateAmnio()
      await this.validateResearchAmnio()

      const isValid = await this.amnioTreatmentValidations()
      if (!isValid) return

      const locationText = this.wound?.location?.location_text

      if (typeof locationText === 'string') {
        this.wound.location.location_text = locationText.trim()
      }

      if (!this.wound.location.location_text) {
        this.formValid = false
      }

      if (this.formValid) {
        if (!this.isQuickEntry) {
          this.treatment.is_validated = true
        }
        this.treatment.modified = this.$custom.utcNow()
        this.wound.modified = this.$custom.utcNow()
        this.treatment.updated = true
        this.wound.updated = true
        this.treatment.require_vitals = this.require_vitals
        this.treatment.wound_treatment.ongoing_debridement_reason = this.treatment.wound_treatment.ongoing_debridement_reason.toString()

        // 04072025-EF: Per TXP 370: Before emitting the validated treatment we
        //                           recalculate post_debridement_area_debrided and
        //                           post_debridement_area
        this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_area = this.getPostDebridementArea()
        this.treatment.wound_treatment.wound_treatment_wound_size.post_debridement_area_debrided = this.getPostDebridementAreaDebrided()

        // Attempt to populate wound description summary in case it's empty.
        if (!this.wound_treatment.wound_description_summary) await this.generateWoundDescription(false)

        this.isAmnioTouched = false
        this.isAmnioResearchTouched = false
        this.$emit('input', this.wound)
        this.done()
      } else {
        if (!this.amnioValid && this.isProcedureAmnioticTissueGraft) {
          this.amnioInvalidMessages = this.$refs.amnioComponent?.getInvalidFields?.()
          this.showAmnioErrors = true
          this.isTreatmentValidation = true

          return
        }

        this.formInvalidAlert()
      }
    },
    async amnioTreatmentValidations() {
      if (!this.isProcedureAmnioticTissueGraft) return true

      const isValidAmnioArea = this.isValidAmnioArea(this.treatment?.wound_treatment?.wound_treatment_wound_size?.primary_area)

      if (!this.isEtiologyPadPvd && this.treatment.wound_treatment?.wound_treatments_amnios?.is_adequate_circulation) {
        this.treatment.wound_treatment.wound_treatments_amnios.is_adequate_circulation = null
      }

      if (!isValidAmnioArea) {
        await this.invalidPreDebridementWoundAreaAlert()

        return false
      }

      return true
    },
    invalidPreDebridementWoundAreaAlert() {
      return new Promise(resolve => {
        this.$root.confirm({
          titleIconColor: 'error',
          title: 'Wound Treatment Doesn\'t Pass Validation!',
          body: 'An amniotic tissue graft may only be used if the pre-debridement wound area is at least 0.8 cm².',
          cancel: false,
        }).then(() => {
          this.scrollToWoundMeasurements()
          resolve()
        })
      })
    },
    scrollToWoundMeasurements() {
      this.$nextTick(() => {
        const el = this.$refs.woundMeasurements?.$el || this.$refs.woundMeasurements

        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    formInvalidAlert() {
      this.$root.confirm({
        titleIconColor: 'error',
        title: 'Wound Treatment Doesn\'t Pass Validation!',
        body: 'Please complete all required and non-validated fields. You will now automatically scroll to the first non-validated field.',
        cancel: false,
      }).then(() => {
        this.$refs.form.validate()
        this.scrollToInvalidField()
      })
    },
    controlSelectedAlert() {
      this.$root.confirm({
        titleIconColor: 'error',
        title: 'Amniotic Tissue Graft Not Allowed',
        body: 'This patient has been selected by Skilled Wound Care as a control group participant in an amniotic tissue graft study and cannot receive this procedure.',
        cancel: false,
      }).then(result => {
        if (result) {
          this.treatment.procedure_id = null
        }
      })
    },
    scrollToInvalidField() {
      // Highlight non-validated fields and smooth-scroll to first section
      this.highlightForm = false
      this.$nextTick(() => {
        this.highlightForm = true
      })
      const field = this.$refs.woundTitle.closest('.v-dialog').querySelector('.required-field, .v-input--has-state')
      if (field) {
        const section = field.closest('.note-section')
        const top = section ? section.closest('.v-dialog') : null
        if (section && top) {
          // Dynamically measure the sticky header height (including expanded banner)
          const stickyHeader = this.$refs.woundTitle
          const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 125
          const extraPadding = 20
          const scrollOffset = headerHeight + extraPadding

          top.scrollTo({ top: section.offsetTop - scrollOffset, behavior: 'smooth' })
        }
      }
    },
    disabledAmnioUserDialog() {
      this.$root.confirm({
        titleIconColor: 'error',
        title: 'Approval Required',
        body: 'Access to this procedure must be approved by Skilled Wound Care management.',
        cancel: false,
      }).then(() => {
        this.treatment.procedure_id = null
      })
    },
    isValidAmnioArea(area) {
      // Exempt specific users
      const amnioAreaExemptions = [100559, 100570]
      if (amnioAreaExemptions.includes(this.userId)) return true

      const validWoundAreaForAtg = 0.8
      const woundPrimaryArea = Number(area)

      return !Number.isNaN(woundPrimaryArea) && woundPrimaryArea >= validWoundAreaForAtg
    },
    clearReasonsForNoDebridement() {
      if (this.modalState && this.isDomUpdated) {
        this.wound_treatment.debridement_not_performed_reasons = []
        this.wound_treatment.debridement_not_performed_reason_other = null
      }
    },
    handlePreviousOpNote() {
      this.$refs.opNotePrevious.open()
    },
    handlePreviousWoundDescriptionSummary() {
      this.$refs.previousWoundDescriptionSummary.open()
    },
    async openAmnio() {
      const area = this.treatment?.wound_treatment?.wound_treatment_wound_size?.primary_area

      const proceed = this.isValidAmnioArea(area)
        ? true
        : await this.$root.confirm({
          titleIconColor: 'warning',
          title: 'Invalid Wound Area',
          body: 'Amniotic tissue grafts are only eligible for wounds with a primary area '
            + 'of at least 0.8 cm². You may still complete the form, but the treatment cannot '
            + 'be validated if the area is below this threshold.',
          cancel: false,
        })

      if (proceed) {
        this.amnioModal = true

        await this.$nextTick()
        await this.$refs.amnioComponent?.validate?.()
      }
    },
    async openResearch() {
      this.researchModal = true

      await this.$nextTick()
      await this.$refs.researchComponent?.validateForm?.()
    },
    onClickAmnio() {
      setTimeout(() => {
        this.isAmnioTouched = true
      }, 200)
      this.openAmnio()
    },
    onClickResearch() {
      setTimeout(() => {
        this.isAmnioResearchTouched = true
      }, 200)
      this.openResearch()
    },
  },
}
</script>
<style scoped>
.full-width-chip {
  width: 100%;
  display: block;
  margin: 0 auto;
  font-size: 16px;
  min-height: auto !important;
  height: auto !important;
}
::v-deep .full-width-chip .v-chip__content {
  color: black;
}

/* Pain level main container */
.pain-level-radio {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100% !important;
  margin-top: 2px !important;
  padding-bottom: 24px !important; /* Increased the bottom padding to give the error text some breathing room. */
  position: relative !important;
}

.pain-level-radio ::v-deep .d-flex.flex-wrap {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  justify-content: center !important;
  width: 100% !important;
}

.pain-level-radio ::v-deep .v-input__slot {
  display: flex !important;
  justify-content: center !important;
  width: 100% !important;
  margin-bottom: 0 !important;
}

.pain-level-radio ::v-deep .v-radio {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  flex: 0 0 auto !important;
  margin: 0 16px !important; 
}

.pain-level-radio ::v-deep .v-label {
  margin-left: 0 !important;
  margin-top: 6px !important;
  text-align: center !important;
  justify-content: center !important;
  width: 100% !important;
}

.pain-level-radio ::v-deep .v-input--selection-controls__input {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

::v-deep .note-section {
  position: relative !important;
}

::v-deep .pain-level-radio .v-input__append-outer {
  position: absolute !important;
  right: 16px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  margin: 0 !important;
  padding: 0 !important;
  z-index: 2;
  left: auto !important;
  bottom: auto !important;
  display: flex !important;
  align-items: center !important;
}

::v-deep .pain-level-radio .v-text-field__details,
::v-deep .pain-level-radio .v-messages {
  position: absolute !important;
  bottom: 6px !important;
  left: 16px !important;
  margin: 0 !important;
  padding: 0 !important;
  font-size: 13px !important;
  line-height: 1 !important;
  min-height: auto !important;
}

@media screen and (max-width: 960px) {
  ::v-deep .pain-level-radio .v-radio {
    margin: 0 10px !important;
  }
}

</style>
